import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import ItemLocation from "./ItemLocation";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { cloneDeep, every, get, isEmpty, omit } from "lodash";
import { diff } from "deep-object-diff";

export const ISSUE_FIELDS = {
  billableTo: "",
  orderID: "",
  packingSlipID: "",
  vendor: "",
  itemID: "",
  laborID: "",
  inventoryItemID: "",
  quantity: 1,
  type: "",
  // Granular detail, collected only when the issue is against an item we have
  // already delivered. `issueType` is a SERVICE_ISSUE_TYPES key (broken_glass,
  // hardware, ...) and `issueTypeDetails` holds that type's prompt answers, so
  // the same questions get asked every time. Empty for warehouse/QC issues,
  // where `type` above is the whole story.
  issueType: "",
  issueTypeDetails: {},
  psLine: "",
  description: "",
  resolution: "",
  orderRequired: false,
  media: [],
  orders: [],
  comments: [],
  isCritical: false,
  isResolved: false,
  locked: false,
  fromLocations: [],
  toLocations: [],
  // The ServiceIssue this was converted into (CONVERT_ISSUE_TO_SERVICE_ISSUE,
  // scripts/backfill-service-center.mjs). Declared so sanitize() keeps it on
  // read and SAVE_ISSUE's whole-document set keeps it on save.
  migratedToServiceIssue: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(ISSUE_FIELDS);

export const ISSUE_BILLABLE_TO = {
  vendor: {
    label: "Vendor",
    icon: "vendor",
  },
  customer: {
    label: "Customer",
    icon: "customer",
  },
  internal: {
    label: "Job Cost",
    icon: "cost",
  },
  nonbillable: {
    label: "Not Billable",
    icon: "lost",
  },
};
deepFreeze(ISSUE_BILLABLE_TO);

export const ISSUE_TYPES = {
  backorder: {
    label: "issue.backorder",
    icon: "backorder",
    color: "info",
    isCritical: true,
    isResolved: true,
    orderRequired: false,
    billableTo: "nonbillable",
    description: "",
    resolution: "",
    media: [],
  },
  missing: {
    label: "issue.missing",
    icon: "missing",
    color: "warning",
    orderRequired: true,
    isCritical: true,
    description: "",
    media: [],
  },
  damage: {
    label: "issue.damage",
    icon: "damage",
    color: "negative",
    criticalLabel: "issue.unusable",
  },
};
deepFreeze(ISSUE_TYPES);

export const ISSUE_STATUS_TYPES = {
  open: {
    order: 1,
    label: "issue.open",
    icon: "open",
    color: "warning",
  },
  closed: {
    order: 2,
    label: "issue.closed",
    icon: "closed",
    color: "positive",
  },
};
deepFreeze(ISSUE_STATUS_TYPES);

export default class Issue extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    data = sanitize(data, ISSUE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    if (this.data.media) {
      this.data.media = this.media.map((d) => d.data);
    }
  }

  setType(typeID) {
    if (typeID) {
      const type = ISSUE_TYPES[typeID];
      if (!type) {
        throw "Invalid issue type";
      } else {
        const data = omit(
          {
            type: typeID,
            ...type,
          },
          ["label", "icon"]
        );
        Object.assign(this.data, data);
      }
    }
    return this;
  }

  // Permissions

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee &&
      (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      !this.locked &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Issue.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/issues`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(issue) {
    return issue.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Issue(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      billableTo: [
        (a) =>
          (this.type && "billableTo" in this.type) ||
          (a && a in ISSUE_BILLABLE_TO) ||
          "required",
      ],
      description: [
        (a) =>
          (this.type && "description" in this.type) ||
          Boolean(a && a.trim().length) ||
          "required",
      ],
      resolution: [
        (a) =>
          !this.data.isResolved ||
          (this.type && "resolution" in this.type) ||
          Boolean(a && a.trim().length) ||
          "required",
      ],
      itemID: [(a) => Boolean(a && a.trim().length) || "required"],
      psLine: [
        (a) =>
          !this.data.packingSlipID ||
          Boolean(a && a.trim().length) ||
          "required",
      ],
      quantity: [
        (a) =>
          (a > 0 && (!this.item || a <= this.item.quantity)) ||
          "quantityInvalid",
      ],
      type: [(a) => (a && a in ISSUE_TYPES) || "required"],
      vendor: [],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          Issue.sanitize({
            ...this.data,
            orderID: "",
            packingSlipID: "",
            itemID: "",
            laborID: "",
            vendor: "",
          }),
          ISSUE_FIELDS
        )
      )
    );
  }

  isEqual(issue) {
    return (
      this.id === issue.id &&
      isEmpty(
        diff(
          Issue.sanitize(cloneDeep(this.data)),
          Issue.sanitize(cloneDeep(issue.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.media) {
      data.media = data.media.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    Issue.cleanDocuments(data, removeUnsaved);
    const item = this.item;
    if (item) {
      data.quantity = Math.min(data.quantity, item.data.quantity);
    }
    if (data.type === "backorder") {
      data.laborID = "";
    }
    if (data.fromLocations) {
      data.fromLocations = data.fromLocations.filter(
        (l) => new ItemLocation(l).isValid
      );
    }
    if (data.toLocations) {
      data.toLocations = data.toLocations.filter(
        (l) => new ItemLocation(l).isValid
      );
    }
    return data;
  }

  // Formatting

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get billableTo() {
    return ISSUE_BILLABLE_TO[this.data.billableTo];
  }

  get type() {
    const type = ISSUE_TYPES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get orders() {
    return this.data.orders.map((id) => store.state.main.orders[id]);
  }

  get packingSlip() {
    return this.data.packingSlipID
      ? store.state.main.packingSlips[this.data.packingSlipID]
      : null;
  }

  get item() {
    return this.data.itemID
      ? store.state.main.saleItems[this.data.itemID]
      : null;
  }

  get labor() {
    return this.data.laborID
      ? store.state.main.saleLabors[this.data.laborID]
      : null;
  }

  get inventoryItem() {
    return this.data.inventoryItemID
      ? store.state.main.inventoryItems[this.data.inventoryItemID]
      : null;
  }

  get fromLocations() {
    return this.data.fromLocations.map((l) => new ItemLocation(l));
  }

  get toLocations() {
    return this.data.toLocations.map((l) => new ItemLocation(l));
  }

  get media() {
    return this.data.media.map((d) => new DocumentRef(d));
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get status() {
    if (this.data.isResolved) {
      return "closed";
    } else {
      return "open";
    }
  }

  get statusType() {
    const type = ISSUE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}
